import React, { useState, useEffect } from 'react';
import { Table } from '../../components/ui/Table';
import { statusBadge } from '../../components/ui/Button';
import { tradeService } from '../../services/trades';
import { Transaction } from '../../types';
import { formatUSD, formatDate } from '../../utils/formatters';
import { gs } from '../../styles/globalStyles';
import { Ico } from '../../components/ui/Icon';

const TABS = [
  { id: 'all',        label: 'All'          },
  { id: 'deposit',    label: 'Deposits'     },
  { id: 'withdrawal', label: 'Withdrawals'  },
  { id: 'trade_close',label: 'Trades'       },
  { id: 'arbitrage',  label: 'Arbitrage'    },
];

const txIcon = (type: string) => {
  const map: Record<string, string> = {
    deposit: '↓', withdrawal: '↑', trade_open: '▶', trade_close: '■',
    arbitrage: '⚡', compound: '♻',
  };
  return map[type] || '•';
};

const typeColor = (type: string) => {
  const map: Record<string, string> = {
    deposit: 'text-primary', withdrawal: 'text-orange-400',
    trade_open: 'text-blue-400', trade_close: 'text-purple-400',
    arbitrage: 'text-yellow-400', compound: 'text-cyan-400',
  };
  return map[type] || 'text-gray-400';
};

const Transactions: React.FC = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadTransactions(); }, [activeTab]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const params = activeTab !== 'all' ? { type: activeTab, per_page: 30 } : { per_page: 30 };
      const res = await tradeService.getTransactions(params);
      setTransactions(res.data.data || []);
    } catch {} finally { setLoading(false); }
  };

  return (
    <div className="p-3 md:p-6">
      {/* Scrollable tab bar — won't overflow on mobile */}
      <div className="flex gap-1 mb-5 overflow-x-auto scrollbar-none pb-0.5 -mx-1 px-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-display font-semibold whitespace-nowrap transition-all shrink-0 ${
              activeTab === t.id
                ? 'bg-primary text-[var(--on-primary)]'
                : 'bg-gray-800/60 text-gray-400 hover:text-gray-50 border border-gray-800/60'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Table
        loading={loading}
        columns={[
          {
            key: 'type',
            label: 'Type',
            render: r => (
              <div className="flex items-center gap-2">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${typeColor(r.type)}`} style={{ background: 'currentColor', opacity: 0.15 }}>
                  <span style={{ opacity: 1 / 0.15 }}>{txIcon(r.type)}</span>
                </span>
                <span className={`text-xs font-semibold capitalize ${typeColor(r.type)}`}>
                  {r.type.replace(/_/g, ' ')}
                </span>
              </div>
            ),
          },
          { key: 'reference', label: 'Reference', render: r => <span className="font-mono text-[10px] text-gray-500">{r.reference || '—'}</span> },
          { key: 'cryptocurrency', label: 'Asset', render: r => r.cryptocurrency ? <span className="font-mono text-xs font-semibold text-gray-50">{r.cryptocurrency}</span> : <span className="text-gray-700">—</span> },
          {
            key: 'amount_usd',
            label: 'Amount (USD)',
            render: r => {
              const isCredit = ['deposit', 'trade_close', 'arbitrage', 'compound'].includes(r.type);
              return (
                <span className={`font-mono text-xs font-semibold ${isCredit ? 'text-primary' : 'text-orange-400'}`}>
                  {isCredit ? '+' : '-'}{formatUSD(r.amount_usd || 0)}
                </span>
              );
            },
          },
          { key: 'wallet_type', label: 'Account', render: r => <span className={`text-[10px] font-mono font-bold capitalize ${r.wallet_type === 'real' ? 'text-primary' : 'text-blue-400'}`}>{r.wallet_type}</span> },
          { key: 'status', label: 'Status', render: r => statusBadge(r.status) },
          { key: 'created_at', label: 'Date', render: r => <span className="text-[10px] text-gray-400 font-mono">{r.created_at?.slice(0, 10)}</span> },
        ]}
        data={transactions}
        keyExtractor={r => r.id}
        emptyMessage="No transactions found"
      />
    </div>
  );
};

export default Transactions;
